import { areSame, isTruthy } from '../bool';
import { sortCaseIgnore } from '../array';
import { getSize } from '../iterable/getSize';
import { keepOld } from '../iterable/keepOld';
import { get } from '../iterable/get';

import { CUSTOM_PROPERTY_IDS, COMMON_PROPERTY_IDS } from '../../constants/taxonomy';
import { AMOUNT, TAXONOMY_ATTRIBUTE } from '../../constants/product';
import { ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { PLACEHOLDERS } from '../../constants/forms';
import { ATTRIBUTE } from '../../constants/attributes';
import { NONE } from '../../constants';
import { DATA } from '../../data';

const OPTION_FORMATTERS = {};

export function getOptions({ category, channel }) {
  const taxonomy = get([channel, 'TAXONOMY', category])(DATA);

  if (!taxonomy) {
    return [get([channel, 'TAXONOMY', 'taxonomy', 'ids'])(DATA)];
  }

  const options = [];

  function reduceFullPath(result, id) {
    options.push(result.ids);
    return result[id];
  }

  const remaining = taxonomy.fullPath.reduce(reduceFullPath, get([channel, 'TAXONOMY', 'taxonomy'])(DATA));

  if (remaining && remaining.ids && getSize(remaining.ids) > 0) {
    options.push(remaining.ids);
  }

  return options.map(function mapOptions(categories) {
    return categories.sort(function sortCategories(x, y) {
      return sortCaseIgnore(get([channel, 'TAXONOMY', x, 'name'])(DATA), get([channel, 'TAXONOMY', y, 'name'])(DATA));
    });
  });
}

export function getValues({ category, channel }) {
  const fullPath = get([channel, 'TAXONOMY', category, 'fullPath'])(DATA);

  if (!fullPath) {
    return [undefined];
  }

  return fullPath;
}

export function getOptionFormatter({ channel, scale }) {
  if (!scale || !get([channel, 'SCALES_LOOKUP', scale])(DATA)) return keepOld;

  if (!(channel in OPTION_FORMATTERS)) {
    OPTION_FORMATTERS[channel] = {};
  }

  if (!(scale in OPTION_FORMATTERS[channel])) {
    const scaleData = get([channel, 'SCALES_LOOKUP', scale])(DATA);

    if (scaleData) {
      const { name } = scaleData;
      OPTION_FORMATTERS[channel][scale] = function format(value = '') {
        return `${value} ${name}`;
      };
    } else {
      OPTION_FORMATTERS[channel][scale] = keepOld;
    }
  }

  return OPTION_FORMATTERS[channel][scale];
}

function getAvailablePropertyIds({ channel, taxonomyId }) {
  if (!taxonomyId) return Object.keys(get([channel, 'STANDARD_PROPERTIES'])(DATA));

  const taxonomySpecificData = get([channel, 'TAXONOMY_SPECIFIC_PROPERTIES', taxonomyId])(DATA) || {};
  const [
    extraProperties = [],
    missingProperties = [],
  ] = get('extraProperties', 'missingProperties')(taxonomySpecificData);

  function filterPropertyIds(propertyId) {
    return !missingProperties.includes(propertyId);
  }

  return COMMON_PROPERTY_IDS[channel].filter(filterPropertyIds).concat(extraProperties);
}

function getAvailableScaleIds({ channel, propertyId, taxonomyId }) {
  const taxonomySpecificData = get([channel, 'TAXONOMY_SPECIFIC_PROPERTIES', taxonomyId])(DATA) || {};
  const overridePropertyData = get(['overrides', propertyId])(taxonomySpecificData) || {};
  const standardPropertyData = get([channel, 'STANDARD_PROPERTIES', propertyId])(DATA) || {};
  const extraScaleIds = get('extraScales')(overridePropertyData) || [];
  const missingScaleIds = get('missingScales')(overridePropertyData) || [];
  const standardScaleIds = get('scaleIds')(standardPropertyData) || [];

  function filterScaleIds(scaleId) {
    return !missingScaleIds.includes(scaleId);
  }

  return standardScaleIds.filter(filterScaleIds).concat(extraScaleIds);
}

function getStandardOptionIdsForScale({ channel, scaleId }) {
  return get([channel, 'SCALES_LOOKUP', scaleId, 'suggestedOptionIds'])(DATA) || null;
}

function getAllPossibleOptions({ channel, propertyId, scaleId = null }) {
  const propertyKey = String(propertyId);
  const scaleKey = scaleId ? String(scaleId) : 'none';
  return get([channel, 'SUGGESTED_OPTIONS_LOOKUP', propertyKey, scaleKey])(DATA);
}

function expandOptions({ channel, optionIds, propertyId, scaleId }) {
  const allPossibleOptions = getAllPossibleOptions({ channel, propertyId, scaleId });

  if (!getSize(allPossibleOptions)) return [];

  function reduceOptionIds(result, optionId) {
    function findOption({ id }) {
      return id === optionId;
    }

    const option = allPossibleOptions.find(findOption);

    if (option) {
      result.push(option);
    }

    return result;
  }

  return optionIds.reduce(reduceOptionIds, []);
}

export function getUiState(config = {}, forVariations = true) {
  const propertiesKey = forVariations ? 'availableVariations' : 'availableAttributes';
  let result = {
    availableScales: [],
    availableOptions: [],
  };

  if (!config.channel || !config.taxonomyId) return result;

  const { channel } = config;
  const taxonomyId = String(config.taxonomyId);
  const taxonomySpecificData = get([channel, 'TAXONOMY_SPECIFIC_PROPERTIES', taxonomyId])(DATA) || {};
  const availablePropertyIds = getAvailablePropertyIds({ channel, taxonomyId });

  function getCustomDisplayName(propertyId) {
    return get(['overrides', propertyId, 'displayName'])(taxonomySpecificData) || false;
  }

  function mapAvailableProperties(propertyId) {
    const isCustomProperty = CUSTOM_PROPERTY_IDS[channel].indexOf(propertyId) !== -1;

    if (isCustomProperty && config.propertyId !== propertyId) return null;

    const {
      attributes,
      displayName,
      isMultivalued,
      name,
      required,
      variations,
    } = get([channel, 'STANDARD_PROPERTIES', propertyId])(DATA) || {};

    const property = {
      attributes,
      displayName: isCustomProperty
        ? config.displayName
        : getCustomDisplayName(propertyId) || displayName,
      id: propertyId,
      isMultivalued,
      name: isCustomProperty ? config.displayName : name,
      required,
      variations,
    };

    if (typeof get(['overrides', propertyId, 'required'])(taxonomySpecificData) === 'boolean') {
      property.required = get(['overrides', propertyId, 'required'])(taxonomySpecificData);
    }

    return property;
  }

  const allAvailableProperties = availablePropertyIds
    .map(mapAvailableProperties)
    .filter(isTruthy)
    .sort(function sortAttributes(x, y) {
      return y.required - x.required;
    });

  function filteAvailableProperties(property) {
    return get(forVariations ? 'variations' : 'attributes')(property);
  }

  result = {
    ...result,
    [propertiesKey]: allAvailableProperties.filter(filteAvailableProperties),
  };

  if (!config.propertyId) return result;

  function findProperty(item) {
    return String(get('id')(item)) === String(config.propertyId);
  }

  if (!result[propertiesKey].find(findProperty)) {
    return result;
  }

  const availableScaleIds = getAvailableScaleIds({ channel, propertyId: config.propertyId, taxonomyId });

  function mapScaleIds(scaleId) {
    return get([channel, 'SCALES_LOOKUP', scaleId])(DATA) || {};
  }

  result = {
    ...result,
    availableScales: availableScaleIds.map(mapScaleIds),
  };

  const readyForPropertyLevelOptions = !!(
    config.taxonomyId &&
    config.propertyId &&
    getSize(result.availableScales) === 0
  );

  const readyForScaleLevelOptions = !!(
    config.taxonomyId &&
    config.propertyId &&
    getSize(result.availableScales) > 0 &&
    config.scaleId
  );

  let availableOptionIds = [];

  if (readyForPropertyLevelOptions) {
    availableOptionIds = (
      get(['overrides', config.propertyId, 'suggestedOptionIds'])(taxonomySpecificData) ||
      get([channel, 'STANDARD_PROPERTIES', config.propertyId, 'suggestedOptionIds'])(DATA) ||
      []
    );
  } else if (readyForScaleLevelOptions) {
    availableOptionIds = (
      get(['overrides', config.propertyId, 'scales', config.scaleId, 'suggestedOptionIds'])(taxonomySpecificData) ||
      getStandardOptionIdsForScale({ channel, scaleId: config.scaleId }) ||
      []
    );
  }

  if (readyForPropertyLevelOptions && !forVariations) {
    const selectedValueIds = (
      get(['overrides', config.propertyId, 'selectedValueIds'])(taxonomySpecificData) ||
      get([channel, 'STANDARD_PROPERTIES', config.propertyId, 'selectedValueIds'])(DATA) ||
      []
    );

    result.required = typeof get(['overrides', config.propertyId, 'required'])(taxonomySpecificData) === 'boolean'
      ? get(['overrides', config.propertyId, 'required'])(taxonomySpecificData)
      : get([channel, 'STANDARD_PROPERTIES', config.propertyId, 'required'])(DATA) || false;

    if (result.required && getSize(selectedValueIds) > 0) {
      result.selectedOptions = selectedValueIds;
    }
  }

  return {
    ...result,
    readyForOptions: readyForPropertyLevelOptions || readyForScaleLevelOptions,
    availableOptions: expandOptions({
      channel,
      optionIds: availableOptionIds,
      propertyId: config.propertyId,
      scaleId: config.scaleId,
    }),
  };
}

export function getAvailable({ category, channel, property, scale }) {
  const {
    availableScales = [],
    availableOptions = [],
    availableVariations = [],
  } = getUiState({ channel, taxonomyId: category, propertyId: property, scaleId: scale });

  function formatProperty({ displayName, id, name }) {
    return {
      id,
      name: displayName || name,
    };
  }

  return {
    availableScales: availableScales.map(formatProperty),
    availableOptions: availableOptions.map(formatProperty),
    availableVariations: availableVariations.map(formatProperty),
  };
}

export function getAttributeType(attribute = {}) {
  if (getSize(attribute.availableScales) > 0) {
    return TAXONOMY_ATTRIBUTE.SCALED;
  }

  if (getSize(attribute.availableOptions) === 0) {
    return undefined;
  }

  if (attribute.isMultivalued) {
    return TAXONOMY_ATTRIBUTE.MULTI;
  }

  if (attribute.totalOptions === 2 && getSize(attribute.availableOptions) === 2) {
    return TAXONOMY_ATTRIBUTE.BOOL;
  }

  return TAXONOMY_ATTRIBUTE.SINGLE;
}

export function getAttribute({ category, channel, property, scale = null, withNone = false }) {
  const result = getUiState({ channel, taxonomyId: category, propertyId: property, scaleId: scale }, false);

  if (get([channel, 'STANDARD_PROPERTIES', property])(DATA)) {
    const {
      name = '',
      displayName = '',
      required = false,
      attributes = true,
      variations = false,
      isMultivalued = false,
    } = get([channel, 'STANDARD_PROPERTIES', property])(DATA);

    result.attributes = attributes;
    result.displayName = displayName;
    result.isMultivalued = isMultivalued;
    result.propertyId = property;
    result.name = name;
    result.required = required;
    result.variations = variations;
    result.totalOptions = getSize(getAllPossibleOptions({ channel, propertyId: property, scaleId: scale }));
    result.type = getAttributeType(result);
  }

  if (
    withNone &&
    getSize(result.availableOptions) &&
    getSize(result.availableOptions) !== getSize(result.selectedOptions)
  ) {
    result.availableOptions.unshift({ id: -1, name: NONE });
  }

  return result;
}

export function getName({ category, channel }) {
  const placeholder = PLACEHOLDERS[ATTRIBUTE.CATEGORY];

  switch (channel) {
    case ETSY: {
      switch (category) {
        case -1:
        case AMOUNT.MINUS_ONE: {
          return NONE;
        }

        case 0:
        case AMOUNT.ZERO: {
          return placeholder;
        }

        default: {
          break;
        }
      }

      break;
    }

    case FAIRE:
    case SHOPIFY: {
      switch (category) {
        case AMOUNT.MINUS_ONE: {
          return NONE;
        }

        case null: {
          return placeholder;
        }

        default: {
          break;
        }
      }

      break;
    }

    default: {
      break;
    }
  }

  return get([channel, 'TAXONOMY', category, 'name'])(DATA) || placeholder;
}

export function getCategories({ category, channel }) {
  return get([channel, 'TAXONOMY', category, 'fullPath'])(DATA) || [];
}

export function getTopTaxonomyId({ category, channel }) {
  return get([channel, 'TAXONOMY', category, 'fullPath', 0])(DATA) || 0;
}

export function findTaxonomyAttributeById({ options, id }) {
  function findOption(option) {
    return option.id === id;
  }

  return options.find(findOption);
}

export function hasSuggestedOptions(attribute) {
  return getSize(get('suggestedOptionIds')(attribute)) > 0;
}

export function findAnalogue(props, match) {
  return function find(item) {
    let result = false;

    for (const key in props) {
      if (props.hasOwnProperty(key)) {
        const itemValue = get(key)(item);
        const valueToCompare = props[key];

        result = typeof match === 'function'
          ? match(itemValue, valueToCompare)
          : areSame(itemValue, valueToCompare);
      }
    }

    return result;
  };
}

export function findAnalogueScale({ channel, scale }) {
  const name = get([channel, 'SCALES_LOOKUP', scale, 'name'])(DATA);
  return findAnalogue({ name });
}
